import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Home, BarChart3, UserCog, FileText, Bell, ChevronDown, Users, AlertTriangle, CheckCircle, Info, TrendingUp, TrendingDown, Clock, Mail, MessageCircle, Award, FileBarChart, UserPlus, Settings as SettingsIcon, X, Check, Archive, Filter, Search, Video, Send, ThumbsUp, Star, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type AlertType = 'urgent' | 'warning' | 'info' | 'success';
type AlertCategory = 'learner' | 'system' | 'achievement' | 'report';
interface Alert {
  id: number;
  type: AlertType;
  category: AlertCategory;
  title: string;
  message: string;
  timestamp: string;
  date: Date;
  read: boolean;
  actionable: boolean;
  completed?: boolean;
  deleted?: boolean;
  learnerName?: string;
  learnerEmail?: string;
  module?: string;
}
export function AlertsPage() {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<'all' | AlertType>('all');
  const [selectedCategory, setSelectedCategory] = useState<'all' | AlertCategory | 'completed' | 'deleted'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'unread' | 'all'>('all');
  const [dateRange, setDateRange] = useState<'all' | 'today' | '7days' | '30days'>('all');
  const [activeCard, setActiveCard] = useState<'total' | 'urgent' | 'completed' | 'deleted' | null>(null);
  const [alerts, setAlerts] = useState<Alert[]>([{
    id: 1,
    type: 'urgent',
    category: 'learner',
    title: 'Learner Struggling - Immediate Attention Required',
    message: 'John Smith has failed JavaScript Basics 3 times with declining scores (68% → 65% → 58%). High misinformation rate detected.',
    timestamp: '5 minutes ago',
    date: new Date(Date.now() - 5 * 60 * 1000),
    read: false,
    actionable: true,
    learnerName: 'John Smith',
    learnerEmail: 'john.smith@example.com',
    module: 'JavaScript Basics'
  }, {
    id: 2,
    type: 'urgent',
    category: 'learner',
    title: 'At-Risk Learner - No Activity for 7 Days',
    message: 'Michael Chen has been inactive for 7 days. Last assessment score was 62% in CSS Fundamentals.',
    timestamp: '1 hour ago',
    date: new Date(Date.now() - 60 * 60 * 1000),
    read: false,
    actionable: true,
    learnerName: 'Michael Chen',
    learnerEmail: 'mchen@example.com',
    module: 'CSS Fundamentals'
  }, {
    id: 6,
    type: 'urgent',
    category: 'learner',
    title: 'CSS Fundamentals has 68% failure rate across all learners',
    message: '42 of 62 learners (68%) are failing CSS Fundamentals on first attempt. Average score is 54%, well below the 70% passing threshold. Common struggle areas: Flexbox (82% error rate), Grid Layout (76% error rate), and Positioning (71% error rate).',
    timestamp: '30 minutes ago',
    date: new Date(Date.now() - 30 * 60 * 1000),
    read: false,
    actionable: true,
    module: 'CSS Fundamentals'
  }, {
    id: 3,
    type: 'success',
    category: 'achievement',
    title: 'Outstanding Performance - Recognition Opportunity',
    message: 'Sarah Johnson achieved 95%+ on all 4 assessments on first attempt. Completed course 2 weeks ahead of schedule.',
    timestamp: '2 hours ago',
    date: new Date(Date.now() - 2 * 60 * 60 * 1000),
    read: false,
    actionable: true,
    learnerName: 'Sarah Johnson',
    learnerEmail: 'sarah.j@example.com',
    module: 'All Modules'
  }, {
    id: 4,
    type: 'warning',
    category: 'learner',
    title: 'Engagement Pattern Alert',
    message: 'Lisa Wong is taking unusually long between attempts (10+ days). Average score is 75% but engagement suggests time management issues.',
    timestamp: '5 hours ago',
    date: new Date(Date.now() - 5 * 60 * 60 * 1000),
    read: true,
    actionable: true,
    learnerName: 'Lisa Wong',
    learnerEmail: 'lisa.w@example.com',
    module: 'Responsive Design'
  }, {
    id: 5,
    type: 'info',
    category: 'system',
    title: 'Monthly Analytics Report Generated',
    message: 'Your monthly analytics report for December 2024 is ready for download.',
    timestamp: '1 day ago',
    date: new Date(Date.now() - 24 * 60 * 60 * 1000),
    read: true,
    actionable: false
  }, {
    id: 7,
    type: 'info',
    category: 'system',
    title: 'Scheduled Platform Maintenance',
    message: 'Platform maintenance is scheduled for Saturday, January 18th from 2:00 AM – 6:00 AM EST. During this window, assessments and reporting features will be temporarily unavailable. Please plan accordingly.',
    timestamp: '3 hours ago',
    date: new Date(Date.now() - 3 * 60 * 60 * 1000),
    read: false,
    actionable: false
  }]);
  // Modal states
  const [messageModal, setMessageModal] = useState<{
    isOpen: boolean;
    alert: Alert | null;
  }>({
    isOpen: false,
    alert: null
  });
  const [meetingModal, setMeetingModal] = useState<{
    isOpen: boolean;
    alert: Alert | null;
  }>({
    isOpen: false,
    alert: null
  });
  const [kudosModal, setKudosModal] = useState<{
    isOpen: boolean;
    alert: Alert | null;
  }>({
    isOpen: false,
    alert: null
  });
  const userMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);
  const markAsRead = (id: number) => {
    setAlerts(alerts.map(alert => alert.id === id ? {
      ...alert,
      read: true
    } : alert));
  };
  const markAllAsRead = () => {
    setAlerts(alerts.map(alert => alert.category === 'system' ? {
      ...alert,
      read: true
    } : {
      ...alert,
      completed: true,
      read: true
    }));
  };
  const completeAlert = (id: number) => {
    setAlerts(alerts.map(alert => alert.id === id ? {
      ...alert,
      completed: true,
      read: true
    } : alert));
  };
  const softDeleteAlert = (id: number) => {
    setAlerts(alerts.map(alert => alert.id === id ? {
      ...alert,
      deleted: true,
      read: true
    } : alert));
  };
  const deleteAlert = (id: number) => {
    setAlerts(alerts.filter(alert => alert.id !== id));
  };
  const filteredAlerts = alerts.filter(alert => {
    // Handle category-based status filters (Completed/Deleted from dropdown)
    if (selectedCategory === 'completed') {
      if (!alert.completed) return false;
    } else if (selectedCategory === 'deleted') {
      if (!alert.deleted) return false;
    } else if (activeCard === 'completed') {
      if (!alert.completed) return false;
    } else if (activeCard === 'deleted') {
      if (!alert.deleted) return false;
    } else {
      // Hide completed and deleted alerts from normal views
      if (alert.completed || alert.deleted) return false;
    }
    // Filter by read status based on view mode
    if (viewMode === 'unread' && alert.read) return false;
    const matchesType = selectedFilter === 'all' || alert.type === selectedFilter;
    const matchesCategory = selectedCategory === 'all' || selectedCategory === 'completed' || selectedCategory === 'deleted' || alert.category === selectedCategory;
    const matchesSearch = alert.title.toLowerCase().includes(searchQuery.toLowerCase()) || alert.message.toLowerCase().includes(searchQuery.toLowerCase()) || alert.learnerName && alert.learnerName.toLowerCase().includes(searchQuery.toLowerCase());
    // Date range filter
    let matchesDate = true;
    if (dateRange !== 'all') {
      const now = new Date();
      const alertDate = alert.date;
      if (dateRange === 'today') {
        const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        matchesDate = alertDate >= startOfDay;
      } else if (dateRange === '7days') {
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        matchesDate = alertDate >= sevenDaysAgo;
      } else if (dateRange === '30days') {
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        matchesDate = alertDate >= thirtyDaysAgo;
      }
    }
    return matchesType && matchesCategory && matchesSearch && matchesDate;
  });
  const unreadCount = alerts.filter(a => !a.read && !a.completed && !a.deleted).length;
  const readCount = alerts.filter(a => a.read).length;
  const urgentCount = alerts.filter(a => a.type === 'urgent' && !a.read && !a.completed && !a.deleted).length;
  const completedCount = alerts.filter(a => a.completed).length;
  const deletedCount = alerts.filter(a => a.deleted).length;
  const resetFilters = () => {
    setSelectedFilter('all');
    setSelectedCategory('all');
    setSearchQuery('');
    setDateRange('all');
  };
  const handleCardClick = (card: 'total' | 'urgent' | 'completed' | 'deleted') => {
    if (activeCard === card) {
      // Toggle off — reset to defaults
      setActiveCard(null);
      setViewMode('all');
      resetFilters();
      return;
    }
    setActiveCard(card);
    resetFilters();
    switch (card) {
      case 'total':
        setViewMode('all');
        break;
      case 'urgent':
        setViewMode('all');
        setSelectedFilter('urgent');
        break;
      case 'completed':
        setViewMode('all');
        break;
      case 'deleted':
        setViewMode('all');
        break;
    }
  };
  const getAlertIcon = (type: AlertType) => {
    switch (type) {
      case 'urgent':
        return <AlertTriangle size={20} className="text-[#DB3725]" data-id="element-1807" data-mp="l7105ylzj5r8" />;
      case 'warning':
        return <Clock size={20} className="text-yellow-600" data-id="element-1808" data-mp="3kuaej30tfuip" />;
      case 'success':
        return <CheckCircle size={20} className="text-[#007558]" data-id="element-1809" data-mp="3oukjcccvgevz" />;
      case 'info':
        return <Info size={20} className="text-[#30718D]" data-id="element-1810" data-mp="23pt36tnh98v1" />;
    }
  };
  const getAlertColor = (type: AlertType) => {
    switch (type) {
      case 'urgent':
        return 'border-[#DB3725] bg-[#DB3725]/5';
      case 'warning':
        return 'border-yellow-500 bg-white';
      case 'success':
        return 'border-[#007558] bg-[#007558]/5';
      case 'info':
        return 'border-[#30718D] bg-[#30718D]/5';
    }
  };
  const getRecipientName = (alert: Alert) => alert.learnerName || 'Team';
  const getRecipientFirstName = (alert: Alert) => alert.learnerName ? alert.learnerName.split(' ')[0] : 'Team';
  return <div className="flex-1 bg-gray-50 min-h-screen" data-id="element-1811" data-mp="1tz7v8flwyrzg">
      {/* Send Message Modal */}
      <AnimatePresence data-id="element-1812" data-mp="1yqg85lmgcvhd">
        {messageModal.isOpen && messageModal.alert && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setMessageModal({
        isOpen: false,
        alert: null
      })} data-id="element-1813" data-mp="1ysj56pm0vovl">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="bg-white rounded-xl shadow-2xl max-w-lg w-full" onClick={e => e.stopPropagation()} data-id="element-1814" data-mp="yojpda8bb672">
              <div className="flex items-center justify-between p-6 border-b border-gray-100" data-id="element-1815" data-mp="fstknf8zvvph">
                <div className="flex items-center gap-3" data-id="element-1816" data-mp="b00huwx36xg1">
                  <div className="w-12 h-12 rounded-lg bg-[#30718D] flex items-center justify-center" data-id="element-1817" data-mp="61ypfldrtlrw">
                    <Mail size={24} className="text-white" data-id="element-1818" data-mp="135lokoad94x" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-1819" data-mp="1118td8eszsi">
                    Send Message
                  </h2>
                </div>
                <button onClick={() => setMessageModal({
              isOpen: false,
              alert: null
            })} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1820" data-mp="3o8ms74lclwue">
                  <X size={20} className="text-gray-500" data-id="element-1821" data-mp="3ilt9po7c28vy" />
                </button>
              </div>
              <div className="p-6" data-id="element-1822" data-mp="34mlawwf312sa">
                <p className="text-gray-600 mb-1" data-id="element-1823" data-mp="34nmius0sx95s">
                  Your message has been successfully sent to
                </p>
                <p className="text-lg font-bold text-gray-900 mb-6" data-id="element-1824" data-mp="15350uive5ui">
                  {getRecipientName(messageModal.alert)}
                </p>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-1825" data-mp="62uulhw1lfvj">
                  <p className="text-sm text-gray-500 mb-2" data-id="element-1826" data-mp="3njmfwh8mz2w8">Message Preview:</p>
                  <p className="text-gray-700 leading-relaxed" data-id="element-1827" data-mp="3sj7eurnt3b8y">
                    "Hi {getRecipientFirstName(messageModal.alert)}, I noticed
                    your recent assessment activity and wanted to reach out to
                    discuss your progress. Let's schedule a time to connect."
                  </p>
                </div>
              </div>
              <div className="flex gap-3 p-6 border-t border-gray-100" data-id="element-1828" data-mp="29iyd62sycxpv">
                <button onClick={() => setMessageModal({
              isOpen: false,
              alert: null
            })} className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium" data-id="element-1829" data-mp="24l6kv7oeb9l5">
                  Cancel
                </button>
                <button onClick={() => setMessageModal({
              isOpen: false,
              alert: null
            })} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium" data-id="element-1830" data-mp="1qmqhnqpqs17r">
                  <Send size={18} data-id="element-1831" data-mp="1umquj6x704cp" />
                  Send
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Schedule Meeting Modal */}
      <AnimatePresence data-id="element-1832" data-mp="3625t2f51v49x">
        {meetingModal.isOpen && meetingModal.alert && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setMeetingModal({
        isOpen: false,
        alert: null
      })} data-id="element-1833" data-mp="589cddefnj8w">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="bg-white rounded-xl shadow-2xl max-w-lg w-full" onClick={e => e.stopPropagation()} data-id="element-1834" data-mp="k5a8ru7kk2dp">
              <div className="flex items-center justify-between p-6 border-b border-gray-100" data-id="element-1835" data-mp="xyrh0iut1sof">
                <div className="flex items-center gap-3" data-id="element-1836" data-mp="12xch8ia24ccv">
                  <div className="w-12 h-12 rounded-lg bg-[#30718D] flex items-center justify-center" data-id="element-1837" data-mp="17vw3ykwh5s6a">
                    <Video size={24} className="text-white" data-id="element-1838" data-mp="1bwzkl9mcjs8k" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-1839" data-mp="2wfa1evkdd673">
                    Schedule Meeting
                  </h2>
                </div>
                <button onClick={() => setMeetingModal({
              isOpen: false,
              alert: null
            })} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1840" data-mp="2sezwznc86nuc">
                  <X size={20} className="text-gray-500" data-id="element-1841" data-mp="2igk71noxpkmf" />
                </button>
              </div>
              <div className="p-6" data-id="element-1842" data-mp="3g4at2rimru25">
                <p className="text-gray-600 mb-1" data-id="element-1843" data-mp="3b2lex304309u">Schedule a meeting with</p>
                <p className="text-lg font-bold text-gray-900 mb-6" data-id="element-1844" data-mp="36cepj0xrzbqj">
                  {getRecipientName(meetingModal.alert)}
                </p>
                <div className="space-y-4" data-id="element-1845" data-mp="31cilx2szpmbi">
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-1846" data-mp="31bh7qpgogjpk">
                    <p className="text-sm text-gray-500 mb-2" data-id="element-1847" data-mp="2peghhqarmp9">Meeting Topic:</p>
                    <p className="text-gray-700 font-medium" data-id="element-1848" data-mp="3u0ei86endc7r">
                      {meetingModal.alert.title}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4" data-id="element-1849" data-mp="1w6k1ybbpu2oz">
                    <div data-id="element-1850" data-mp="1w84wi0y15lmu">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1851" data-mp="1ievrech47ma4">
                        Date
                      </label>
                      <input type="date" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#30718D] focus:border-transparent" defaultValue={new Date().toISOString().split('T')[0]} data-id="element-1852" data-mp="1nc5qkjub3cb5" />
                    </div>
                    <div data-id="element-1853" data-mp="zfrnfi4fkipb">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1854" data-mp="zgjimru48ef2">
                        Time
                      </label>
                      <input type="time" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#30718D] focus:border-transparent" defaultValue="10:00" data-id="element-1855" data-mp="14gytncqxd9tv" />
                    </div>
                  </div>
                  <div data-id="element-1856" data-mp="38wkizr269xkx">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1857" data-mp="38xvn5rzj57ck">
                      Duration
                    </label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#30718D] focus:border-transparent" data-id="element-1858" data-mp="33vmteteu219e">
                      <option data-id="element-1859" data-mp="3cy5v3a6vu7u2">15 minutes</option>
                      <option selected data-id="element-1860" data-mp="3cwb9dtnsebox">30 minutes</option>
                      <option data-id="element-1861" data-mp="3rm8we69zcvcj">45 minutes</option>
                      <option data-id="element-1862" data-mp="3rp4upvpqpm1i">1 hour</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="flex gap-3 p-6 border-t border-gray-100" data-id="element-1863" data-mp="4ivwic27bkr1">
                <button onClick={() => setMeetingModal({
              isOpen: false,
              alert: null
            })} className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium" data-id="element-1864" data-mp="h0s2yoz4r91">
                  Cancel
                </button>
                <button onClick={() => setMeetingModal({
              isOpen: false,
              alert: null
            })} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium" data-id="element-1865" data-mp="2qtk5wrch8y30">
                  <Calendar size={18} data-id="element-1866" data-mp="30lhm6ciceg8l" />
                  Schedule
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Send Kudos Modal */}
      <AnimatePresence data-id="element-1867" data-mp="2ctj8jlvf88sa">
        {kudosModal.isOpen && kudosModal.alert && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setKudosModal({
        isOpen: false,
        alert: null
      })} data-id="element-1868" data-mp="2ctswz50ti7vv">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="bg-white rounded-xl shadow-2xl max-w-lg w-full" onClick={e => e.stopPropagation()} data-id="element-1869" data-mp="3sa1eqtb69yfn">
              <div className="flex items-center justify-between p-6 border-b border-gray-100" data-id="element-1870" data-mp="1h2aofqui9s0">
                <div className="flex items-center gap-3" data-id="element-1871" data-mp="3tmpn9kuxcmj3">
                  <div className="w-12 h-12 rounded-lg bg-[#007558] flex items-center justify-center" data-id="element-1872" data-mp="3tk3ghnfuyb1x">
                    <ThumbsUp size={24} className="text-white" data-id="element-1873" data-mp="3jnqem2a5sqw3" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-1874" data-mp="3jq37hahgglnx">
                    Give Kudos
                  </h2>
                </div>
                <button onClick={() => setKudosModal({
              isOpen: false,
              alert: null
            })} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1875" data-mp="3anty4nxxayc4">
                  <X size={20} className="text-gray-500" data-id="element-1876" data-mp="18v5sy91py0tp" />
                </button>
              </div>
              <div className="p-6" data-id="element-1877" data-mp="3bn4g94qhyk12">
                <p className="text-gray-600 mb-1" data-id="element-1878" data-mp="3bmcsuh71zslv">Send kudos to celebrate</p>
                <p className="text-lg font-bold text-gray-900 mb-6" data-id="element-1879" data-mp="37mc70bhmkf4s">
                  {getRecipientName(kudosModal.alert)}
                </p>
                <div className="bg-[#007558]/5 rounded-lg p-4 border border-[#007558]/20 mb-4" data-id="element-1880" data-mp="32s7yhnspnz1l">
                  <div className="flex items-center gap-2 mb-2" data-id="element-1881" data-mp="2xtdewjmx66gf">
                    <Star size={18} className="text-[#007558]" data-id="element-1882" data-mp="2sx68kpqm2sxy" />
                    <p className="text-sm font-medium text-[#007558]" data-id="element-1883" data-mp="2nwscm62ab9ga">
                      Achievement:
                    </p>
                  </div>
                  <p className="text-gray-700" data-id="element-1884" data-mp="2jvyn58971mla">{kudosModal.alert.title}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-1885" data-mp="2eygcndfj2zb6">
                  <p className="text-sm text-gray-500 mb-2" data-id="element-1886" data-mp="i30miq96dpps">Kudos Message:</p>
                  <p className="text-gray-700 leading-relaxed" data-id="element-1887" data-mp="d4z7qhis8ck4">
                    "Congratulations {getRecipientFirstName(kudosModal.alert)}!
                    🎉 Your hard work and dedication have really paid off. Keep
                    up the amazing work!"
                  </p>
                </div>
                <div className="mt-4" data-id="element-1888" data-mp="ufmby58aomvz">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1889" data-mp="ugnvyz7agjpu">
                    Add a personal note (optional)
                  </label>
                  <textarea className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007558] focus:border-transparent resize-none" rows={3} placeholder="Add your personal message..." data-id="element-1890" data-mp="1qxfwkm3j7r3" />
                </div>
              </div>
              <div className="flex gap-3 p-6 border-t border-gray-100" data-id="element-1891" data-mp="fpoue84vevfr">
                <button onClick={() => setKudosModal({
              isOpen: false,
              alert: null
            })} className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium" data-id="element-1892" data-mp="fnkfkv5foc0p">
                  Cancel
                </button>
                <button onClick={() => setKudosModal({
              isOpen: false,
              alert: null
            })} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium" data-id="element-1893" data-mp="3o7kovl0hond9">
                  <ThumbsUp size={18} data-id="element-1894" data-mp="3s7l2j3frkuj9" />
                  Send Kudos
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200" data-id="element-1895" data-mp="30hqav04fosrf">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1896" data-mp="3e7jzbhc11b0m">
          <div className="flex items-center h-16" data-id="element-1897" data-mp="39gk5iky7f92c">
            <div className="text-[#DB3725] font-bold text-xl" data-id="element-1898" data-mp="39fsapccflfd4">LOGO</div>

            <nav className="flex items-center gap-4 sm:gap-8 ml-6 sm:ml-12" data-id="element-1899" data-mp="2lq7p0low8405">
              <Link to="/admin" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1900" data-mp="2gs64rxfqdyn3">
                <Home size={18} data-id="element-1901" data-mp="w92dp0jjjd1o" />
                <span className="hidden sm:inline" data-id="element-1902" data-mp="w810oqiysghb">Admin Hub</span>
              </Link>
              <Link to="/analytics" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1903" data-mp="111vkdtykkvge">
                <BarChart3 size={18} data-id="element-1904" data-mp="s7qx1kina25m" />
                <span className="hidden sm:inline" data-id="element-1905" data-mp="s8s5ric6uck6">Analytics</span>
              </Link>
              <Link to="/feedback" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1906" data-mp="1fz62vn9mr1lm">
                <FileText size={18} data-id="element-1907" data-mp="1jx3pcic0n9lb" />
                <span className="hidden sm:inline" data-id="element-1908" data-mp="160glrwpk5l6f">Reports</span>
              </Link>
              <Link to="/assessment" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1909" data-mp="1ay8kb9kpk5jl">
                <UserCog size={18} data-id="element-1910" data-mp="3qol0m4l6gvtv" />
                <span className="hidden sm:inline" data-id="element-1911" data-mp="3vopju2dh04ns">Users</span>
              </Link>
            </nav>

            <div className="flex items-center gap-2 sm:gap-4 ml-auto" data-id="element-1912" data-mp="esztqn1xrgdy">
              <Link to="/alerts" className="relative p-2 text-[#30718D] hover:text-[#265a70] transition-colors" data-id="element-1913" data-mp="evcltt1er74q">
                <Bell size={20} data-id="element-1914" data-mp="60xv8ubfga6b" />
                {unreadCount > 0 && <span className="absolute top-1 right-1 w-2 h-2 bg-[#DB3725] rounded-full" data-id="element-1915" data-mp="61ptye0bmo0t"></span>}
              </Link>
              <div className="relative" ref={userMenuRef} data-id="element-1916" data-mp="13o7rk7lm8l4">
                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors" data-id="element-1917" data-mp="xsdsdiam1fas">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-medium" data-id="element-1918" data-mp="orfkgox1wnmx">
                    LT
                  </div>
                  <span className="hidden sm:inline text-sm font-medium text-gray-900" data-id="element-1919" data-mp="jup6c74t4245">
                    Linda Taylor
                  </span>
                  <ChevronDown size={16} className="text-gray-600" data-id="element-1920" data-mp="34iafpxryqgug" />
                </button>

                {isUserMenuOpen && <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50" data-id="element-1921" data-mp="2ufy4xpo4c4si">
                    <div className="py-2" data-id="element-1922" data-mp="2zbluw7afd1z2">
                      <Link to="/profile" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1923" data-mp="34c9mov6h9muc">
                        <Users size={18} className="text-gray-600" data-id="element-1924" data-mp="395m70t44j7ha" />
                        <span className="font-medium text-gray-900" data-id="element-1925" data-mp="3d4bgwbpye6w6">
                          Profile
                        </span>
                      </Link>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1926" data-mp="3n3k9b0567dgf">
                        <Home size={18} className="text-gray-600" data-id="element-1927" data-mp="3s12lczehi8sn" />
                        <span className="font-medium text-gray-900" data-id="element-1928" data-mp="1spt021qr6vli">
                          Learner Hub
                        </span>
                      </button>
                      <Link to="/admin" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1929" data-mp="1xq7b7snnilnr">
                        <UserCog size={18} className="text-gray-600" data-id="element-1930" data-mp="1kpn0jgngz67c" />
                        <span className="font-medium text-gray-900" data-id="element-1931" data-mp="1zlkvwwpkk6ii">
                          Admin Hub
                        </span>
                      </Link>
                      <Link to="/settings" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1932" data-mp="1uouf275xx6w1">
                        <SettingsIcon size={18} className="text-gray-600" data-id="element-1933" data-mp="23o7o4yq5jtg8" />
                        <span className="font-medium text-gray-900" data-id="element-1934" data-mp="2ic2kcp3dcfy2">
                          Settings
                        </span>
                      </Link>
                      <div className="border-t border-gray-200 my-2" data-id="element-1935" data-mp="2de0yxxih04jf"></div>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1936" data-mp="2rdtnbm6iy8o8">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-1937" data-mp="2n9lvhg4rscls">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" data-id="element-1938" data-mp="3awpqfa5p9l9e"></path>
                          <polyline points="16 17 21 12 16 7" data-id="element-1939" data-mp="2wyrhika5eh1a"></polyline>
                          <line x1="21" y1="12" x2="9" y2="12" data-id="element-1940" data-mp="31x2swzg3czlm"></line>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-1941" data-mp="2n86joz2atgjf">
                          Logout
                        </span>
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8" data-id="element-1942" data-mp="2iao9l5cjov9u">
        {/* Header */}
        <div data-phoebe-tour="alerts" className="mb-8" data-id="element-1943" data-mp="24avmrl452z75">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4" data-id="element-1944" data-mp="299zue7bf0ovd">
            <div data-id="element-1945" data-mp="2orzri4kqhgw8">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2" data-id="element-1946" data-mp="2oqezompt0c22">
                Action Items
              </h1>
              <p className="text-gray-600 text-sm sm:text-base" data-id="element-1947" data-mp="2jt6cg7psthty">
                Stay updated with important alerts and take action on learner
                needs
              </p>
            </div>
            <button onClick={markAllAsRead} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#30718D] hover:bg-[#30718D]/10 rounded-lg transition-colors self-start" data-id="element-1948" data-mp="2arfc7ie1237h">
              <CheckCircle size={16} className="text-[#007558]" data-id="element-1949" data-mp="210cbihp0fq8q" />
              Mark all as complete
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4" data-id="element-1950" data-mp="1s1qrei3w435u">
            <motion.button onClick={() => handleCardClick('total')} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className={`bg-white rounded-xl p-4 shadow-sm border-2 text-left transition-all ${activeCard === 'total' ? 'border-[#30718D] ring-1 ring-[#30718D]/20' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1951" data-mp="1n24fcbzsssym">
              <div className="flex items-center justify-between" data-id="element-1952" data-mp="7khod8e9ppsq">
                <div data-id="element-1953" data-mp="bl9xm1n53up0">
                  <div className="text-2xl font-bold text-gray-900" data-id="element-1954" data-mp="bjqi4d9b5noa">
                    {alerts.filter(a => !a.completed && !a.deleted).length}
                  </div>
                  <div className="text-sm text-gray-600" data-id="element-1955" data-mp="3fyq8rt6mza9t">
                    Total Active Alerts
                  </div>
                </div>
                <Bell size={24} className="text-[#30718D]" data-id="element-1956" data-mp="3jzs1rs9ymq3c" />
              </div>
            </motion.button>

            <motion.button onClick={() => handleCardClick('urgent')} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.15
          }} className={`bg-white rounded-xl p-4 shadow-sm border-2 text-left transition-all ${activeCard === 'urgent' ? 'border-[#DB3725] ring-1 ring-[#DB3725]/20' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1957" data-mp="3osuyg9a1afnr">
              <div className="flex items-center justify-between" data-id="element-1958" data-mp="1x0ndg7gaq1pe">
                <div data-id="element-1959" data-mp="1x2hqkzlbspix">
                  <div className="text-2xl font-bold text-[#DB3725]" data-id="element-1960" data-mp="21z869lt7xj3t">
                    {urgentCount}
                  </div>
                  <div className="text-sm text-gray-600" data-id="element-1961" data-mp="1n9j2ditqr0zu">Urgent</div>
                </div>
                <AlertTriangle size={24} className="text-[#DB3725]" data-id="element-1962" data-mp="1s1kaqtnsg7kl" />
              </div>
            </motion.button>

            <motion.button onClick={() => handleCardClick('completed')} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className={`bg-white rounded-xl p-4 shadow-sm border-2 text-left transition-all ${activeCard === 'completed' ? 'border-[#007558] ring-1 ring-[#007558]/20' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1963" data-mp="1eaysn765s1nr">
              <div className="flex items-center justify-between" data-id="element-1964" data-mp="lnfieg1dzbfj">
                <div data-id="element-1965" data-mp="lp8pfwfwvxo2">
                  <div className="text-2xl font-bold text-[#007558]" data-id="element-1966" data-mp="lps8jghass3o">
                    {completedCount}
                  </div>
                  <div className="text-sm text-gray-600" data-id="element-1967" data-mp="unklwf3glpof">Completed</div>
                </div>
                <CheckCircle size={24} className="text-[#007558]" data-id="element-1968" data-mp="18ndaa3rijtt8" />
              </div>
            </motion.button>

            <motion.button onClick={() => handleCardClick('deleted')} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.25
          }} className={`bg-white rounded-xl p-4 shadow-sm border-2 text-left transition-all ${activeCard === 'deleted' ? 'border-gray-500 ring-1 ring-gray-500/20' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1969" data-mp="14n334nrzixcs">
              <div className="flex items-center justify-between" data-id="element-1970" data-mp="1nayddxoe1t15">
                <div data-id="element-1971" data-mp="1uhap6dv7lxb1">
                  <div className="text-2xl font-bold text-gray-500" data-id="element-1972" data-mp="1udwjavnc75yu">
                    {deletedCount}
                  </div>
                  <div className="text-sm text-gray-600" data-id="element-1973" data-mp="24efakbh8doqs">Deleted</div>
                </div>
                <Archive size={24} className="text-gray-500" data-id="element-1974" data-mp="294vmts9bf4bk" />
              </div>
            </motion.button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 mb-6" data-id="element-1975" data-mp="2e3f95trtmi4g">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 flex-wrap" data-id="element-1976" data-mp="2i48x2n59jwxc">
            <div className="flex-1 min-w-0 relative" data-id="element-1977" data-mp="2n56fn0u6qszd">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-1978" data-mp="2s49n0rxn1bai" />
              <input type="text" placeholder="Search notifications..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent text-sm" data-id="element-1979" data-mp="sr5h6h2js7zt" />
            </div>

            <div className="flex items-center gap-2 flex-wrap" data-id="element-1980" data-mp="lf7p05i92nwa">
              <Filter size={16} className="text-gray-600" data-id="element-1981" data-mp="gge8e47o2grw" />
              <select value={selectedFilter} onChange={e => setSelectedFilter(e.target.value as any)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent flex-1 sm:flex-none" data-id="element-1982" data-mp="ggwa3gd3ln77">
                <option value="all" data-id="element-1983" data-mp="145pb3r8kul8o">All Types</option>
                <option value="urgent" data-id="element-1984" data-mp="143w6silfsd3u">Urgent</option>
                <option value="warning" data-id="element-1985" data-mp="z5ulrs4ga3pq">Warning</option>
                <option value="success" data-id="element-1986" data-mp="u7t338axs6es">Success</option>
                <option value="info" data-id="element-1987" data-mp="u6zstxcrqyrw">Info</option>
              </select>
            </div>

            <div className="flex items-center gap-2 flex-wrap" data-id="element-1988" data-mp="3jy68y6zvc91p">
              <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value as any)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent flex-1 sm:flex-none" data-id="element-1989" data-mp="3ey9zhs8ejper">
                <option value="all" data-id="element-1990" data-mp="1cfmqzoeg2rai">All Categories</option>
                <option value="learner" data-id="element-1991" data-mp="xpov0ma9wxar">Learner Alerts</option>
                <option value="system" data-id="element-1992" data-mp="12nqh7g2zxcqg">System Alerts</option>
                <option value="achievement" data-id="element-1993" data-mp="12o06f1g7vfv3">Kudos</option>
                <option value="completed" data-id="element-1994" data-mp="1qd1jj1zzvf6p">Completed</option>
                <option value="deleted" data-id="element-1995" data-mp="1qbgo7a5uvs7s">Deleted</option>
              </select>
            </div>

            <div className="flex items-center gap-2 flex-wrap" data-id="element-1996" data-mp="1vabeex65kj1x">
              <Calendar size={16} className="text-gray-600" data-id="element-1997" data-mp="1gec19846rspx" />
              <select value={dateRange} onChange={e => setDateRange(e.target.value as any)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent flex-1 sm:flex-none" data-id="element-1998" data-mp="1ldye3gg3r6y7">
                <option value="all" data-id="element-1999" data-mp="a0cra1xpthfu">All Time</option>
                <option value="today" data-id="element-2000" data-mp="2o6auxqq6p24l">Today</option>
                <option value="7days" data-id="element-2001" data-mp="2o8x4fvwmxrpg">Last 7 Days</option>
                <option value="30days" data-id="element-2002" data-mp="2j6o8cqogu9j4">Last 30 Days</option>
              </select>
            </div>
          </div>
        </div>

        {/* Alerts List */}
        <div className="space-y-3" data-id="element-2003" data-mp="2x563vakm4qbl">
          <AnimatePresence mode="popLayout" data-id="element-2004" data-mp="2t5nkwkccqpbe">
            {filteredAlerts.map((alert, index) => <motion.div key={alert.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            delay: index * 0.05
          }} className={`bg-white rounded-xl shadow-sm border-l-4 overflow-hidden ${getAlertColor(alert.type)} ${!alert.read ? 'border-l-4' : 'border-l-2 opacity-75'}`} data-id="element-2005" data-mp="25ft822s190r5" data-mpi={index}>
                <div className="p-4 sm:p-6" data-id="element-2006" data-mp="1lqiq0i4te3pv">
                  <div className="flex items-start gap-3 sm:gap-4" data-id="element-2007" data-mp="1hra0jpgt4s04">
                    <div className="flex-shrink-0 mt-1" data-id="element-2008" data-mp="1hoxi7b750fmj">
                      {getAlertIcon(alert.type)}
                    </div>

                    <div className="flex-1 min-w-0" data-id="element-2009" data-mp="3lttfcmfstewc">
                      {/* Title and Actions - Stack on mobile */}
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4 mb-2" data-id="element-2010" data-mp="3qmmjp97cul7w">
                        <div className="flex-1" data-id="element-2011" data-mp="3vjmq5l849pzj">
                          <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-2012" data-mp="2xx5tmi77jgeu">
                            <h3 className="text-sm sm:text-base font-bold text-gray-900" data-id="element-2013" data-mp="32vqt2femxw28">
                              {alert.title}
                            </h3>
                          </div>
                          <p className="text-xs sm:text-sm text-gray-700 leading-relaxed" data-id="element-2014" data-mp="3ctwu6usmay61">
                            {alert.message}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0" data-id="element-2015" data-mp="s3o0xb1h3642">
                          {!alert.completed && !alert.deleted && <>
                              {alert.category !== 'system' && <button onClick={() => completeAlert(alert.id)} className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm font-medium border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" title="Mark as completed" data-id="element-2016" data-mp="2cnt84qtjd4e2">
                                  <CheckCircle size={14} className="text-[#007558]" data-id="element-2017" data-mp="1xuznkqzepy2j" />
                                  <span className="hidden sm:inline" data-id="element-2018" data-mp="1svmqldetnam8">
                                    Mark Completed
                                  </span>
                                </button>}
                              <button onClick={() => softDeleteAlert(alert.id)} className="p-1.5 text-gray-400 hover:text-[#DB3725] hover:bg-[#DB3725]/10 rounded-lg transition-colors" title="Delete" data-id="element-2019" data-mp="1jz5fubadder0">
                                <X size={16} data-id="element-2020" data-mp="3e8zkdt2yh7lk" />
                              </button>
                            </>}
                          {alert.completed && <span className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm font-medium bg-[#007558]/10 text-[#007558] rounded-lg" data-id="element-2021" data-mp="2i63jvewt7fae">
                              <CheckCircle size={14} data-id="element-2022" data-mp="28gtqlvq8bqkp" />
                              <span className="hidden sm:inline" data-id="element-2023" data-mp="28gajzb614olz">
                                Completed
                              </span>
                            </span>}
                          {alert.deleted && <span className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm font-medium bg-gray-100 text-gray-500 rounded-lg" data-id="element-2024" data-mp="1zekh996p638w">
                              <Archive size={14} data-id="element-2025" data-mp="1pj0zrrsvr73o" />
                              <span className="hidden sm:inline" data-id="element-2026" data-mp="1pkc2doala8t7">Deleted</span>
                            </span>}
                        </div>
                      </div>

                      {/* Learner Info - Stack on mobile */}
                      {alert.learnerName && <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 mb-3 text-sm" data-id="element-2027" data-mp="3ou0r6qsg3szu">
                          <div className="flex items-center gap-2" data-id="element-2028" data-mp="1d8hzkun39z15">
                            <Users size={14} className="text-gray-500" data-id="element-2029" data-mp="1895dwd7yey05" />
                            <span className="font-medium text-gray-700" data-id="element-2030" data-mp="1mxjfkktmwgf4">
                              {alert.learnerName}
                            </span>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-2031" data-mp="1i503xteibvcy">
                            <Mail size={14} className="text-gray-500" data-id="element-2032" data-mp="1vwmzfr0mkzuo" />
                            <span className="text-gray-600 text-xs sm:text-sm" data-id="element-2033" data-mp="1rxe6uphbn80p">
                              {alert.learnerEmail}
                            </span>
                          </div>
                          {alert.module && <div className="flex items-center gap-2" data-id="element-2034" data-mp="20ym557ju88us">
                              <FileBarChart size={14} className="text-gray-500" data-id="element-2035" data-mp="bhus7scgxfds" />
                              <span className="text-gray-600 text-xs sm:text-sm" data-id="element-2036" data-mp="6j9ubzkov7si">
                                {alert.module}
                              </span>
                            </div>}
                        </div>}

                      {/* Action Buttons and Timestamp */}
                      <div className="pt-3 border-t border-gray-200" data-id="element-2037" data-mp="13kdqb18cgzjs">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" data-id="element-2038" data-mp="1hjde66o19aew">
                          {alert.actionable ? <div className="flex flex-wrap items-center gap-2" data-id="element-2039" data-mp="1cl25hz9h55y9">
                              <button onClick={() => setMessageModal({
                          isOpen: true,
                          alert
                        })} className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors" data-id="element-2040" data-mp="1qgno9op46ti8">
                                <Mail size={14} data-id="element-2041" data-mp="2a5y5x88f7oiz" />
                                <span className="hidden sm:inline" data-id="element-2042" data-mp="2a6ps5sg4o9wl">
                                  Send Message
                                </span>
                              </button>
                              <button onClick={() => setMeetingModal({
                          isOpen: true,
                          alert
                        })} className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-2043" data-mp="2otofbb7yci5i">
                                <Video size={14} data-id="element-2044" data-mp="254xjvklbwxol" />
                                <span className="hidden sm:inline" data-id="element-2045" data-mp="2532wlzml4thc">
                                  Schedule Meeting
                                </span>
                              </button>
                              {alert.type === 'success' && <button onClick={() => setKudosModal({
                          isOpen: true,
                          alert
                        })} className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium border border-[#007558] bg-[#007558]/10 text-[#007558] rounded-lg hover:bg-[#007558]/20 transition-colors" data-id="element-2046" data-mp="213ltvdyjmafv">
                                  <ThumbsUp size={14} data-id="element-2047" data-mp="182w2o51jqgvr" />
                                  <span className="hidden sm:inline" data-id="element-2048" data-mp="1391kx75i4bzb">
                                    Send Kudos
                                  </span>
                                </button>}
                            </div> : <div data-id="element-2049" data-mp="tcybdg8blb2c" />}
                          <span className="flex items-center gap-1.5 text-xs text-gray-400 self-start sm:self-auto" data-id="element-2050" data-mp="t9sjjt8b3v2x">
                            <Clock size={12} data-id="element-2051" data-mp="1m0kmd9jvnhz8" />
                            {alert.timestamp}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>)}
          </AnimatePresence>

          {filteredAlerts.length === 0 && <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-200" data-id="element-2052" data-mp="5mkn80suvwd1">
              {activeCard === 'completed' ? <>
                  <CheckCircle size={48} className="mx-auto text-[#007558] mb-4" data-id="element-2053" data-mp="pkk9yraz9q5" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-2054" data-mp="2cce9te3fbh81">
                    No completed alerts
                  </h3>
                  <p className="text-gray-600 mb-4" data-id="element-2055" data-mp="2m90thzk0k28h">
                    Alerts you mark as completed will appear here.
                  </p>
                  <button onClick={() => {
              setActiveCard(null);
              setViewMode('all');
              resetFilters();
            }} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#30718D] hover:bg-[#30718D]/10 rounded-lg transition-colors" data-id="element-2056" data-mp="2r091cr5p3fjj">
                    <Bell size={16} data-id="element-2057" data-mp="350jszwetwy5r" />
                    View all alerts
                  </button>
                </> : activeCard === 'deleted' ? <>
                  <Archive size={48} className="mx-auto text-gray-400 mb-4" data-id="element-2058" data-mp="1alh9qggdicba" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-2059" data-mp="1anka9uf5jnu9">
                    No deleted alerts
                  </h3>
                  <p className="text-gray-600 mb-4" data-id="element-2060" data-mp="360we9fwvphmt">
                    Alerts you delete will appear here.
                  </p>
                  <button onClick={() => {
              setActiveCard(null);
              setViewMode('all');
              resetFilters();
            }} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#30718D] hover:bg-[#30718D]/10 rounded-lg transition-colors" data-id="element-2061" data-mp="311tarzwjrjgy">
                    <Bell size={16} data-id="element-2062" data-mp="2nbps1iwvm7w9" />
                    View all alerts
                  </button>
                </> : <>
                  <Bell size={48} className="mx-auto text-gray-400 mb-4" data-id="element-2063" data-mp="2i9qrshwfnr3j" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-2064" data-mp="2w4t3xmrvicos">
                    No notifications found
                  </h3>
                  <p className="text-gray-600 mb-4" data-id="element-2065" data-mp="23mxj3vhf5jkb">
                    Try adjusting your filters or search query
                  </p>
                  {(selectedFilter !== 'all' || selectedCategory !== 'all' || dateRange !== 'all' || searchQuery || activeCard) && <button onClick={() => {
              setActiveCard(null);
              setViewMode('all');
              resetFilters();
            }} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#30718D] hover:bg-[#30718D]/10 rounded-lg transition-colors" data-id="element-2066" data-mp="1pgn9zew2q0qd">
                      <X size={16} data-id="element-2067" data-mp="1kj3vqezdlnyf" />
                      Clear all filters
                    </button>}
                </>}
            </div>}
        </div>
      </div>
    </div>;
}